import { useState, useEffect, useCallback, useMemo } from 'react';
import PortalNav from '@/components/feature/PortalNav';
import FileCard, { SkeletonCard } from './components/FileCard';
import PreviewModal from './components/PreviewModal';
import VideoPlayerModal from './components/VideoPlayerModal';
import Sidebar from './components/Sidebar';
import { listLibrary, getLibraryTree, recordMaterialView, type LibraryListParams } from '@/api/library';
import type { MaterialItem } from '@/api/materialInfo';
import type { CategoryNodeWithCount } from '@/api/library';

export default function LibraryPage() {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [sortBy, setSortBy] = useState('latest');
  const [previewMaterial, setPreviewMaterial] = useState<MaterialItem | null>(null);
  const [videoMaterial, setVideoMaterial] = useState<MaterialItem | null>(null);

  // 数据状态
  const [materials, setMaterials] = useState<MaterialItem[]>([]);
  const [total, setTotal] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryTree, setCategoryTree] = useState<CategoryNodeWithCount[]>([]);

  const PAGE_SIZE = 20;

  // 安全防护：禁止右键、拖选、快捷键复制/F12
  useEffect(() => {
    const preventContext = (e: Event) => e.preventDefault();

    const handleKeyDown = (e: KeyboardEvent) => {
      // F12
      if (e.key === 'F12') {
        e.preventDefault();
        return;
      }
      // Ctrl+A / Ctrl+C
      if ((e.ctrlKey || e.metaKey) && (e.key === 'a' || e.key === 'c')) {
        e.preventDefault();
      }
    };

    document.addEventListener('contextmenu', preventContext);
    document.addEventListener('selectstart', preventContext);
    document.addEventListener('copy', preventContext);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('contextmenu', preventContext);
      document.removeEventListener('selectstart', preventContext);
      document.removeEventListener('copy', preventContext);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // 加载分类树
  useEffect(() => {
    getLibraryTree().then(setCategoryTree).catch(() => {});
  }, []);

  // 从分类树构建 ID → 标签 映射
  const categoryLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const primary of categoryTree) {
      map.set(primary.id, primary.label);
      for (const sub of primary.subCategories) {
        map.set(sub.id, sub.label);
        for (const model of sub.models) {
          map.set(model.id, model.label);
        }
      }
    }
    return map;
  }, [categoryTree]);

  // 加载资料列表
  const fetchMaterials = useCallback(async (page: number) => {
    setLoading(true);
    setError(null);
    try {
      const params: LibraryListParams = {
        pageNumber: page,
        pageSize: PAGE_SIZE,
        sortBy: sortBy as 'latest' | 'popular' | 'az',
      };
      if (search.trim()) params.title = search.trim();
      if (activeCategory !== 'all') {
        if (activeCategory.startsWith('model:')) {
          params.modelCategory = activeCategory.slice(6);
        } else {
          params.category = activeCategory;
        }
      }
      const result = await listLibrary(params);
      setMaterials(result.list);
      setTotal(result.total);
      setPageNumber(result.pageNumber);
    } catch (err: any) {
      setError(err.message || '加载资料失败');
    } finally {
      setLoading(false);
    }
  }, [search, activeCategory, sortBy]);

  // 搜索/分类/排序变化时重新加载（重置到第1页）
  useEffect(() => {
    fetchMaterials(1);
  }, [fetchMaterials]);

  // 翻页
  const handlePageChange = (newPage: number) => {
    fetchMaterials(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePreview = (material: MaterialItem) => {
    // 记录查看日志到后端
    recordMaterialView(material.id).catch(() => {});

    if (material.isVideo) {
      setVideoMaterial(material);
    } else {
      setPreviewMaterial(material);
    }
  };

  const handleDownload = (material: MaterialItem) => {
    if (!material.downloadAllowed || material.isVideo) return;
    const token = localStorage.getItem('agent_token');
    const params = new URLSearchParams({ id: material.id });
    if (token) params.set('token', token);
    window.open(`/api/agentfront/download.htm?${params.toString()}`, '_blank');
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="min-h-screen bg-gray-50">
      <PortalNav searchValue={search} onSearchChange={setSearch} showSearch />

      <div className="max-w-screen-xl mx-auto px-4 md:px-6 py-6 flex gap-6">
        <Sidebar
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
          sortBy={sortBy}
          onSortChange={setSortBy}
          categoryTree={categoryTree}
        />

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          {/* Top Bar */}
          <div className="flex items-center justify-between mb-5">
            <p className="text-sm text-gray-500">
              Showing <span className="font-semibold text-gray-800">{materials.length}</span> of{' '}
              <span className="font-semibold text-gray-800">{total}</span> materials
            </p>
            {/* Mobile Sort */}
            <div className="flex items-center gap-2 lg:hidden">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 text-gray-700 focus:outline-none focus:border-blue-400 bg-white cursor-pointer"
              >
                <option value="latest">Latest Upload</option>
                <option value="popular">Most Popular</option>
                <option value="az">A → Z</option>
              </select>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-red-50 mb-4">
                <i className="ri-error-warning-line text-3xl text-red-400"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-1">加载失败</h3>
              <p className="text-sm text-gray-400 mb-4">{error}</p>
              <button
                onClick={() => fetchMaterials(pageNumber)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                重试
              </button>
            </div>
          )}

          {/* Grid */}
          {!error && (
            <>
              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
                </div>
              ) : materials.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-center">
                  <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gray-100 mb-4">
                    <i className="ri-folder-open-line text-4xl text-gray-300"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-1">No materials found</h3>
                  <p className="text-sm text-gray-400 mb-4">Try adjusting your search or filters</p>
                  <button
                    onClick={() => { setSearch(''); setActiveCategory('all'); }}
                    className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Clear filters
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {materials.map((m) => (
                      <FileCard key={m.id} material={m} onPreview={handlePreview} onDownload={handleDownload} categoryLabelMap={categoryLabelMap} />
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-8 pb-4">
                      <button
                        onClick={() => handlePageChange(pageNumber - 1)}
                        disabled={pageNumber <= 1}
                        className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm transition-colors cursor-pointer ${
                          pageNumber <= 1
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-600 hover:bg-white hover:border-gray-300 border border-gray-200'
                        }`}
                      >
                        <i className="ri-arrow-left-s-line"></i>
                      </button>
                      {Array.from({ length: Math.min(totalPages, 7) }).map((_, i) => {
                        // 简单分页：显示前7页或最后7页
                        let displayPage: number;
                        if (totalPages <= 7) {
                          displayPage = i + 1;
                        } else if (pageNumber <= 4) {
                          displayPage = i + 1;
                        } else if (pageNumber >= totalPages - 3) {
                          displayPage = totalPages - 6 + i;
                        } else {
                          displayPage = pageNumber - 3 + i;
                        }
                        if (displayPage > totalPages) return null;
                        return (
                          <button
                            key={displayPage}
                            onClick={() => handlePageChange(displayPage)}
                            className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                              displayPage === pageNumber
                                ? 'text-white'
                                : 'text-gray-600 hover:bg-white border border-gray-200'
                            }`}
                            style={displayPage === pageNumber ? { background: '#0069BD' } : {}}
                          >
                            {displayPage}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => handlePageChange(pageNumber + 1)}
                        disabled={pageNumber >= totalPages}
                        className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm transition-colors cursor-pointer ${
                          pageNumber >= totalPages
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-600 hover:bg-white hover:border-gray-300 border border-gray-200'
                        }`}
                      >
                        <i className="ri-arrow-right-s-line"></i>
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </main>
      </div>

      <PreviewModal material={previewMaterial} onClose={() => setPreviewMaterial(null)} />
      <VideoPlayerModal material={videoMaterial} onClose={() => setVideoMaterial(null)} />
    </div>
  );
}
